/**
 * NO INTERNET BRO — a ragdoll destruction sandbox.
 *
 * The internet is down, so BRO takes it out on the office. Built on the same
 * melonJS + @melonjs/planck-adapter (Box2D) stack as the Ragdoll POC, but the
 * ragdoll here is a *controllable* active-ragdoll: joint motors hold a rough
 * humanoid shape while the player lurches it around with WASD, jumps (H),
 * pauses (Space) and swings a haymaker (G). The room is furnished with a desk,
 * a computer and an office chair — every piece is a cluster of dynamic bodies
 * held together by breakable weld joints, so a hard-enough whack rips them
 * apart. Thrash until nothing is left standing.
 *
 * Everything is drawn by a single Painter that reads each body's live
 * position + angle, exactly like the Ragdoll POC — no per-renderable transform
 * bookkeeping.
 */
import { Application, Renderable, Rect, video } from "melonjs";
import { PlanckAdapter } from "@melonjs/planck-adapter";

// Fixed design resolution. melonJS ("scale: auto" + "fit") letterboxes this to
// any viewport, preserving the aspect ratio — so the room never distorts and
// furniture never runs off-screen on a phone, and rotation is handled for free.
// The whole layout is authored against these two numbers.
const W = 1080;
const H = 680;

const FLOOR_H = 46; // thickness of the floor slab
const FLOOR_TOP = H - FLOOR_H; // y of the walkable surface

const COLORS = {
  // room
  wallTop: "#12203a",
  wallBot: "#0c1526",
  floor: "#241a12",
  floorFace: "#3a2a1a",
  baseboard: "#1a2740",
  // bro
  skin: "#ffd9a0",
  shirt: "#e622c5", // inkegg pink
  arm: "#f0705a",
  leg: "#2f6bd6",
  // furniture
  deskTop: "#8a5a32",
  deskLeg: "#6f471f",
  monitor: "#20242c",
  screen: "#33ff33",
  tower: "#3a3f4a",
  chair: "#2a3550",
  chairSeat: "#39466b",
  wheel: "#11151f",
};

/**
 * An invisible rigid body. The Painter draws it from its live transform, so
 * draw() is a no-op. `round` renders as an ellipse (head), `screen` overlays a
 * lit monitor face.
 */
class Part extends Renderable {
  constructor(cx, cy, w, h, color, opts = {}) {
    super(cx - w / 2, cy - h / 2, w, h);
    this.w = w;
    this.h = h;
    this.hw = w / 2;
    this.hh = h / 2;
    this.color = color;
    this.round = !!opts.round;
    this.screen = !!opts.screen;
    this.role = opts.role || null;
    this.group = opts.group || null; // "player" | "furniture" | "room"
    this.bodyDef = {
      type: opts.type || "dynamic",
      shapes: [new Rect(0, 0, w, h)],
      fixedRotation: false,
      density: opts.density ?? 1.1,
      friction: opts.friction ?? 0.6,
      restitution: opts.restitution ?? 0.04,
    };
  }
  draw() {
    /* invisible — the Painter renders every body */
  }
}

/** Draws the room + every body each frame from live positions. */
class Painter extends Renderable {
  constructor(parts, onTick) {
    super(0, 0, W, H);
    this.anchorPoint.set(0, 0);
    this.parts = parts;
    this.onTick = onTick;
    this.floating = true;
  }
  update(dt) {
    if (this.onTick) this.onTick(dt);
    return true;
  }
  draw(renderer) {
    // wall (vertical gradient faked with two bands) + floor
    renderer.setColor(COLORS.wallTop);
    renderer.fillRect(0, 0, W, FLOOR_TOP * 0.55);
    renderer.setColor(COLORS.wallBot);
    renderer.fillRect(0, FLOOR_TOP * 0.55, W, FLOOR_TOP - FLOOR_TOP * 0.55);
    renderer.setColor(COLORS.baseboard);
    renderer.fillRect(0, FLOOR_TOP - 10, W, 10);
    renderer.setColor(COLORS.floor);
    renderer.fillRect(0, FLOOR_TOP, W, FLOOR_H);
    renderer.setColor(COLORS.floorFace);
    renderer.fillRect(0, FLOOR_TOP, W, 5);

    for (const p of this.parts) {
      if (p.bodyDef.type === "static") continue; // room slab drawn above
      const cx = p.pos.x + p.hw;
      const cy = p.pos.y + p.hh;
      const angle = p.body ? p.body.getAngle() : 0;
      renderer.save();
      renderer.translate(cx, cy);
      renderer.rotate(angle);
      renderer.setColor(p.color);
      if (p.round) {
        renderer.fillEllipse(0, 0, p.hw, p.hh);
      } else {
        renderer.fillRect(-p.hw, -p.hh, p.w, p.h);
      }
      if (p.screen) {
        renderer.setColor(p.on === false ? "#0b1a0b" : COLORS.screen);
        renderer.fillRect(-p.hw + 5, -p.hh + 5, p.w - 10, p.h - 10);
      }
      renderer.restore();
    }
  }
}

function boot() {
  const app = new Application(W, H, {
    parent: "screen",
    renderer: video.AUTO,
    scale: "auto", // fit the design resolution to the viewport…
    scaleMethod: "fit", // …letterboxed, preserving aspect (mobile-friendly)
    physic: new PlanckAdapter({ gravity: { x: 0, y: 150 } }),
  });
  app.isAlwaysDirty = true;
  app.pauseOnBlur = false;
  app.resumeOnFocus = true;
  app.stopOnBlur = false;

  const adapter = app.world.adapter;
  const planck = adapter.planck;
  const PPM = adapter.pixelsPerMeter;
  const V = (px, py) => new planck.Vec2(px / PPM, py / PPM);

  const parts = []; // everything the Painter draws
  const dynParts = []; // dynamic bodies (player + furniture)
  const playerParts = []; // bro's limbs
  const playerJoints = {}; // named revolute joints
  let furnitureJoints = []; // breakable welds { joint, limit }

  const add = (cx, cy, w, h, color, opts = {}) => {
    const p = new Part(cx, cy, w, h, color, opts);
    parts.push(p);
    if (p.bodyDef.type === "dynamic") dynParts.push(p);
    app.world.addChild(p);
    return p;
  };

  const revolute = (a, b, px, py, name) => {
    const j = new planck.RevoluteJoint(
      { collideConnected: false },
      a.body,
      b.body,
      V(px, py),
    );
    adapter.world.createJoint(j);
    if (name) playerJoints[name] = j;
    return j;
  };

  const weld = (a, b, px, py, limit) => {
    const j = new planck.WeldJoint(
      { collideConnected: false, frequencyHz: 0 },
      a.body,
      b.body,
      V(px, py),
    );
    adapter.world.createJoint(j);
    furnitureJoints.push({ joint: j, limit });
    return j;
  };

  // ── static room ────────────────────────────────────────────────────────────
  add(W / 2, FLOOR_TOP + FLOOR_H / 2, W, FLOOR_H, COLORS.floor, { type: "static" });
  add(-8, H / 2, 20, H * 2, COLORS.wallBot, { type: "static" }); // left wall
  add(W + 8, H / 2, 20, H * 2, COLORS.wallBot, { type: "static" }); // right wall
  add(W / 2, -8, W, 20, COLORS.wallBot, { type: "static" }); // ceiling

  // ── BRO: a controllable active-ragdoll ─────────────────────────────────────
  // Authored around a local origin; buildBro(cx) shifts the whole figure.
  const buildBro = (cx) => {
    const fy = FLOOR_TOP; // feet start on the floor
    const P = (dx, dy, w, h, color, opts) =>
      add(cx + dx, fy + dy, w, h, color, { ...opts, group: "player", role: opts?.role });

    const head = P(0, -330, 46, 46, COLORS.skin, { role: "head", round: true, density: 0.9 });
    const torso = P(0, -250, 58, 104, COLORS.shirt, { role: "torso", density: 1.3 });
    const uArmL = P(-38, -268, 20, 60, COLORS.arm, { role: "uArmL" });
    const lArmL = P(-38, -208, 18, 56, COLORS.arm, { role: "lArmL", density: 1.6 });
    const uArmR = P(38, -268, 20, 60, COLORS.arm, { role: "uArmR" });
    const lArmR = P(38, -208, 18, 56, COLORS.arm, { role: "lArmR", density: 1.6 });
    const thighL = P(-16, -150, 24, 68, COLORS.leg, { role: "thighL" });
    const shinL = P(-16, -80, 22, 66, COLORS.leg, { role: "shinL", friction: 1.4 });
    const thighR = P(16, -150, 24, 68, COLORS.leg, { role: "thighR" });
    const shinR = P(16, -80, 22, 66, COLORS.leg, { role: "shinR", friction: 1.4 });

    playerParts.push(head, torso, uArmL, lArmL, uArmR, lArmR, thighL, shinL, thighR, shinR);

    revolute(head, torso, cx + 0, fy - 300, "neck");
    revolute(torso, uArmL, cx - 30, fy - 292, "shoulderL");
    revolute(uArmL, lArmL, cx - 38, fy - 238, "elbowL");
    revolute(torso, uArmR, cx + 30, fy - 292, "shoulderR");
    revolute(uArmR, lArmR, cx + 38, fy - 238, "elbowR");
    revolute(torso, thighL, cx - 14, fy - 190, "hipL");
    revolute(thighL, shinL, cx - 16, fy - 118, "kneeL");
    revolute(torso, thighR, cx + 14, fy - 190, "hipR");
    revolute(thighR, shinR, cx + 16, fy - 118, "kneeR");

    return { head, torso };
  };

  // Active-ragdoll stiffness: motors hold each joint near its rest angle within
  // a limit, so BRO keeps a rough humanoid shape but still ragdolls when smacked.
  const STIFF = 900;
  const stiffenJoints = () => {
    for (const name in playerJoints) {
      const j = playerJoints[name];
      j.enableLimit(true);
      j.setLimits(-0.7, 0.7);
      j.enableMotor(true);
      j.setMotorSpeed(0);
      j.setMaxMotorTorque(STIFF);
    }
  };

  // ── furniture: clusters of dynamic bodies held by breakable welds ──────────
  const buildDesk = (cx) => {
    const topY = FLOOR_TOP - 96;
    const top = add(cx, topY, 220, 20, COLORS.deskTop, { group: "furniture", density: 0.9 });
    const legL = add(cx - 92, topY + 54, 16, 92, COLORS.deskLeg, { group: "furniture" });
    const legR = add(cx + 92, topY + 54, 16, 92, COLORS.deskLeg, { group: "furniture" });
    weld(top, legL, cx - 92, topY + 10, 900);
    weld(top, legR, cx + 92, topY + 10, 900);
    return { top, topY };
  };

  const buildComputer = (cx, deskTopY) => {
    // monitor + tower resting on the desk, welded down so a whack knocks them off
    const monY = deskTopY - 52;
    const mon = add(cx, monY, 96, 74, COLORS.monitor, {
      group: "furniture",
      screen: true,
      density: 0.8,
    });
    const stand = add(cx, deskTopY - 10, 16, 22, COLORS.tower, { group: "furniture" });
    const tower = add(cx + 150, deskTopY - 40, 40, 78, COLORS.tower, { group: "furniture" });
    weld(mon, stand, cx, monY + 37, 650);
    return { mon, stand, tower };
  };

  const buildChair = (cx) => {
    const seatY = FLOOR_TOP - 78;
    const seat = add(cx, seatY, 74, 18, COLORS.chairSeat, { group: "furniture", density: 0.7 });
    const back = add(cx - 28, seatY - 46, 16, 74, COLORS.chair, { group: "furniture" });
    const post = add(cx, seatY + 34, 12, 44, COLORS.chair, { group: "furniture" });
    const baseL = add(cx - 30, seatY + 62, 34, 12, COLORS.chair, { group: "furniture" });
    const baseR = add(cx + 30, seatY + 62, 34, 12, COLORS.chair, { group: "furniture" });
    const whL = add(cx - 44, seatY + 74, 14, 14, COLORS.wheel, { group: "furniture", round: true });
    const whR = add(cx + 44, seatY + 74, 14, 14, COLORS.wheel, { group: "furniture", round: true });
    weld(seat, back, cx - 28, seatY - 18, 700);
    weld(seat, post, cx, seatY + 12, 600);
    weld(post, baseL, cx - 12, seatY + 62, 500);
    weld(post, baseR, cx + 12, seatY + 62, 500);
    weld(baseL, whL, cx - 44, seatY + 68, 350);
    weld(baseR, whR, cx + 44, seatY + 68, 350);
    return seat;
  };

  // ── build / rebuild the whole scene ────────────────────────────────────────
  let bro = null;
  const clearDynamic = () => {
    release();
    for (const p of dynParts) app.world.removeChildNow(p);
    dynParts.length = 0;
    playerParts.length = 0;
    for (const k in playerJoints) delete playerJoints[k];
    furnitureJoints = [];
    for (let i = parts.length - 1; i >= 0; i--) {
      if (parts[i].bodyDef.type === "dynamic") parts.splice(i, 1);
    }
  };

  const buildScene = () => {
    clearDynamic();
    const desk = buildDesk(W * 0.66);
    buildComputer(W * 0.66, desk.topY);
    buildChair(W * 0.5);
    bro = buildBro(W * 0.28);
    stiffenJoints();
    paused = false;
  };

  const Painter_ = new Painter(parts, (dt) => onFrame(dt));
  app.world.addChild(Painter_, 100);

  // expose for debugging / automated verification
  window.nib = {
    app,
    parts,
    playerParts,
    playerJoints,
    get bro() {
      return bro;
    },
    furnitureCount: () => furnitureJoints.length,
    keys: null,
  };

  // ── input ──────────────────────────────────────────────────────────────────
  const keys = new Set();
  window.nib.keys = keys;
  let paused = false;

  const isGrounded = () => {
    // grounded if a foot is near the floor and not moving up fast
    for (const p of playerParts) {
      if (p.role !== "shinL" && p.role !== "shinR") continue;
      const yPx = (p.pos.y + p.hh);
      const vy = p.body.getLinearVelocity().y;
      if (yPx > FLOOR_TOP - 90 && vy < 4) return true;
    }
    return false;
  };

  const jump = () => {
    if (!isGrounded()) return;
    for (const p of playerParts) {
      const b = p.body;
      b.applyLinearImpulse(new planck.Vec2(0, -26 * b.getMass()), b.getWorldCenter(), true);
    }
  };

  // Haymaker: briefly free the arms and whip them so they smash whatever's near,
  // plus a forward lunge. Direction depends on facing (last horizontal input).
  let attackTimer = 0;
  let facing = 1;
  const ATTACK_TIME = 0.34;
  const attack = () => {
    if (attackTimer > 0) return;
    attackTimer = ATTACK_TIME;
    const arms = ["shoulderL", "elbowL", "shoulderR", "elbowR"];
    for (const n of arms) playerJoints[n]?.enableMotor(false);
    for (const p of playerParts) {
      if (!/Arm/.test(p.role || "")) continue;
      p.body.setAngularVelocity(-facing * 34);
    }
    const t = bro?.torso?.body;
    if (t) t.applyLinearImpulse(new planck.Vec2(facing * 9 * t.getMass(), -2 * t.getMass()), t.getWorldCenter(), true);
  };

  const setPaused = (v) => {
    paused = v;
    const el = document.getElementById("nib-paused");
    if (el) el.hidden = !v;
    if (!v) {
      for (const p of dynParts) p.body.setGravityScale(1);
    }
  };

  // Logical input — shared by the physical keyboard and the on-screen pad.
  const pressKey = (k) => {
    if (k === " ") { setPaused(!paused); return; }
    if (paused) return;
    if (k === "r") { buildScene(); return; }
    if (k === "h") { jump(); return; }
    if (k === "g") { attack(); return; }
    if (k === "a" || k === "arrowleft") facing = -1;
    if (k === "d" || k === "arrowright") facing = 1;
    keys.add(k);
  };
  const releaseKey = (k) => keys.delete(k);
  window.nib.press = pressKey;
  window.nib.release = releaseKey;

  const HANDLED = ["w", "a", "s", "d", "g", "h", " ", "arrowleft", "arrowright", "arrowup", "arrowdown"];
  window.addEventListener("keydown", (ev) => {
    const t = ev.target;
    if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA")) return;
    const k = ev.key.toLowerCase();
    if (HANDLED.includes(k)) ev.preventDefault();
    pressKey(k);
  });
  window.addEventListener("keyup", (ev) => releaseKey(ev.key.toLowerCase()));
  window.addEventListener("blur", () => keys.clear());

  // ── optional on-screen controller ───────────────────────────────────────────
  // Buttons carry data-key; a press/hold maps to the same logical input as the
  // keyboard, so held directions drive the gait and taps fire jump/attack/pause.
  const bindPad = (el) => {
    const k = el.getAttribute("data-key");
    const down = (e) => { e.preventDefault(); el.classList.add("is-down"); pressKey(k); };
    const up = (e) => { e.preventDefault(); el.classList.remove("is-down"); releaseKey(k); };
    el.addEventListener("pointerdown", (e) => { el.setPointerCapture?.(e.pointerId); down(e); });
    el.addEventListener("pointerup", up);
    el.addEventListener("pointercancel", up);
    el.addEventListener("lostpointercapture", up);
    el.addEventListener("contextmenu", (e) => e.preventDefault());
  };
  document.querySelectorAll("[data-key]").forEach(bindPad);

  // Show the pad by default on touch devices; the toggle button flips it either way.
  const stage = document.querySelector(".nib-stage");
  const padToggle = document.getElementById("nib-pad-toggle");
  const setPad = (on) => {
    stage?.classList.toggle("pad-on", on);
    padToggle?.classList.toggle("is-active", on);
    padToggle?.setAttribute("aria-pressed", String(on));
  };
  const isTouch = window.matchMedia?.("(pointer: coarse)").matches || "ontouchstart" in window;
  setPad(isTouch);
  padToggle?.addEventListener("click", () => setPad(!stage?.classList.contains("pad-on")));

  const held = (a, b) => keys.has(a) || (b && keys.has(b));

  // ── pointer drag (grab & fling any limb, like the Ragdoll POC) ─────────────
  let mouseJoint = null;
  const canvas = app.renderer.getCanvas();
  const toWorldPx = (ev) => {
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((ev.clientX - rect.left) / rect.width) * W,
      y: ((ev.clientY - rect.top) / rect.height) * H,
    };
  };
  canvas.addEventListener("pointerdown", (ev) => {
    if (paused) return;
    const { x: px, y: py } = toWorldPx(ev);
    const hits = adapter.queryAABB(new Rect(px - 6, py - 6, 12, 12));
    const target = hits.find((r) => r.bodyDef && r.bodyDef.type === "dynamic");
    if (!target) return;
    mouseJoint = new planck.MouseJoint(
      { maxForce: 4000 * target.body.getMass(), frequencyHz: 5, dampingRatio: 0.8 },
      parts[0].body, // ground
      target.body,
      V(px, py),
    );
    adapter.world.createJoint(mouseJoint);
    canvas.setPointerCapture?.(ev.pointerId);
  });
  window.addEventListener("pointermove", (ev) => {
    if (!mouseJoint) return;
    const { x, y } = toWorldPx(ev);
    mouseJoint.setTarget(V(x, y));
  });
  function release() {
    if (mouseJoint) {
      adapter.world.destroyJoint(mouseJoint);
      mouseJoint = null;
    }
  }
  window.addEventListener("pointerup", release);
  window.addEventListener("pointercancel", release);

  // ── per-frame control + destruction ────────────────────────────────────────
  const MOVE = 220; // horizontal drive acceleration (× mass)
  const VMAX = 18; // cap on torso horizontal speed (m/s-ish)
  const BALANCE = 340; // upright-righting stiffness (× torso mass) so BRO can step
  const BALANCE_DAMP = 60; // angular damping for the balance controller
  const GAIT_SPEED = 9; // rad/s the walk phase advances while moving
  const HIP_AMP = 0.5; // hip swing amplitude (rad, within the ±0.7 joint limit)
  const KNEE_AMP = 0.55; // knee bend during the forward swing (rad)
  const LEG_TORQUE = 1400; // motor torque available to drive the gait
  const LEG_GAIN = 10; // P-gain: motor speed commanded per rad of angle error
  const LEG_MAXSPEED = 14; // cap on leg-motor speed (rad/s)
  let gaitPhase = 0;

  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
  // Position-drive a leg joint toward a target angle (P-controller on a velocity
  // motor): the motor chases `target`, giving BRO an actual, if clumsy, stride.
  const driveJoint = (name, target) => {
    const j = playerJoints[name];
    if (!j) return;
    j.enableMotor(true);
    j.setMaxMotorTorque(LEG_TORQUE);
    j.setMotorSpeed(clamp((target - j.getJointAngle()) * LEG_GAIN, -LEG_MAXSPEED, LEG_MAXSPEED));
  };
  function onFrame(dtMs) {
    const dt = Math.min(dtMs / 1000, 0.05);

    if (paused) {
      for (const p of dynParts) {
        p.body.setGravityScale(0);
        p.body.setLinearVelocity(new planck.Vec2(0, 0));
        p.body.setAngularVelocity(0);
      }
      return;
    }

    // movement — balance upright, drive forward, and step the legs in a gait
    const torso = bro?.torso?.body;
    if (torso) {
      const m = torso.getMass();
      let dir = 0;
      if (held("a", "arrowleft")) dir -= 1;
      if (held("d", "arrowright")) dir += 1;

      // Keep BRO roughly upright (restoring torque + damping) so he can walk
      // instead of toppling. Stays active while standing so he recovers balance.
      torso.applyTorque(
        (-torso.getAngle() * BALANCE - torso.getAngularVelocity() * BALANCE_DAMP) * m,
        true,
      );

      if (dir !== 0) {
        facing = dir;
        const vx = torso.getLinearVelocity().x;
        if (Math.abs(vx) < VMAX) {
          torso.applyForceToCenter(new planck.Vec2(dir * MOVE * m, 0), true);
        }
        // Walk cycle: legs 180° out of phase; each knee bends on its leg's
        // forward swing to clear the floor. Combined with the drive force and
        // the balance torque, this reads as a clumsy stride.
        gaitPhase += dt * GAIT_SPEED;
        const s = Math.sin(gaitPhase);
        driveJoint("hipL", dir * HIP_AMP * s);
        driveJoint("kneeL", KNEE_AMP * Math.max(0, s));
        driveJoint("hipR", -dir * HIP_AMP * s);
        driveJoint("kneeR", KNEE_AMP * Math.max(0, -s));
      } else {
        // Idle: settle the legs straight and hold the stance.
        gaitPhase = 0;
        driveJoint("hipL", 0);
        driveJoint("kneeL", 0);
        driveJoint("hipR", 0);
        driveJoint("kneeR", 0);
      }

      // W = thrash/reach up, S = slam down
      if (held("w", "arrowup")) {
        for (const p of playerParts) {
          if (!/Arm|head/.test(p.role || "")) continue;
          const b = p.body;
          b.applyForceToCenter(new planck.Vec2(0, -180 * b.getMass()), true);
        }
      }
      if (held("s", "arrowdown")) {
        torso.applyForceToCenter(new planck.Vec2(0, 260 * torso.getMass()), true);
      }
    }

    // attack window: restore arm motors when it expires
    if (attackTimer > 0) {
      attackTimer -= dt;
      if (attackTimer <= 0) {
        ["shoulderL", "elbowL", "shoulderR", "elbowR"].forEach((n) => {
          const j = playerJoints[n];
          if (j) {
            j.enableMotor(true);
            j.setMotorSpeed(0);
            j.setMaxMotorTorque(STIFF);
          }
        });
      }
    }

    // furniture destruction — snap any weld whose reaction force exceeds its bond
    const invDt = 1 / dt;
    for (let i = furnitureJoints.length - 1; i >= 0; i--) {
      const { joint, limit } = furnitureJoints[i];
      const rf = joint.getReactionForce(invDt);
      if (Math.hypot(rf.x, rf.y) > limit) {
        adapter.world.destroyJoint(joint);
        furnitureJoints.splice(i, 1);
      }
    }
  }

  // ── UI wiring ───────────────────────────────────────────────────────────────
  document.getElementById("nib-reset")?.addEventListener("click", () => buildScene());
  document.getElementById("nib-pause")?.addEventListener("click", () => setPaused(!paused));

  buildScene();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot);
} else {
  boot();
}
